import React from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Pin, PinOff } from 'lucide-react';
import { useShortcuts } from '../contexts/ShortcutsContext';

/**
 * Pin that floats above open dialogs (Radix overlays sit at z-50), so a
 * record opened in a full-screen sheet can still be pinned without every
 * dialog growing its own button. Only shows for deep targets — page-level
 * pinning uses the button beside the main tab bar.
 */
const FloatingPinButton = () => {
  const { t } = useTranslation();
  const { currentTarget, findPinned, addShortcut, removeShortcut } = useShortcuts();
  const target = currentTarget;
  if (!target?.tab || (target._priority || 0) < 2) return null;

  const pinned = findPinned(target);

  const handle = async () => {
    try {
      if (pinned) {
        await removeShortcut(pinned.id);
        toast.success(t('shortcuts.unpinned'));
      } else {
        await addShortcut(target);
        toast.success(t('shortcuts.pinned'));
      }
    } catch (e) {
      toast.error(e?.response?.status === 409 ? t('shortcuts.limit') : t('shortcuts.saveFailed'));
    }
  };

  return (
    <button
      type="button"
      onClick={handle}
      // The button lives outside any dialog, so Radix would treat the press
      // as an outside-click and close the record before the click lands.
      onPointerDown={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      onTouchStart={(e) => e.stopPropagation()}
      title={pinned ? t('shortcuts.unpin') : `${t('shortcuts.pin')} — ${target.label}`}
      aria-label={pinned ? t('shortcuts.unpin') : t('shortcuts.pin')}
      className={`pointer-events-auto fixed bottom-5 left-5 z-[70] flex h-11 items-center gap-2 rounded-full border px-3 shadow-lg transition-colors ${
        pinned
          ? 'border-amber-300 bg-amber-50 text-amber-700'
          : 'border-slate-200 bg-white text-slate-600 hover:text-slate-900'
      }`}
      data-testid="floating-pin-btn"
    >
      {pinned ? <PinOff className="h-4 w-4" /> : <Pin className="h-4 w-4" />}
      <span className="hidden max-w-[22ch] truncate text-xs font-medium sm:inline">
        {pinned ? t('shortcuts.unpin') : target.label}
      </span>
    </button>
  );
};

export default FloatingPinButton;
